/**
 * SKOP Auth Context
 *
 * Manages wallet-based authentication with backend sessions.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import bs58 from 'bs58';
import {
  requestAuthChallenge,
  verifySignature,
  validateSession,
  logout as apiLogout,
  clearToken,
} from '../lib/backend/client';
import type { SessionUser } from '../lib/backend/types';

interface AuthContextType {
  // State
  user: SessionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  authenticate: () => Promise<boolean>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { publicKey, signMessage, connected, disconnect } = useWallet();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = user !== null;

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  // Clear session if wallet disconnects
  useEffect(() => {
    if (!connected && user) {
      handleLogout();
    }
  }, [connected]);

  /**
   * Check if there's a valid existing session
   * Checks both localStorage token AND HttpOnly cookie
   */
  const checkExistingSession = async () => {
    setIsLoading(true);
    try {
      // Always check with backend - cookie may be valid even if localStorage is empty
      const session = await validateSession();
      if (session.valid && session.user) {
        setUser(session.user);
      } else {
        clearToken(); // Clear localStorage if session invalid
      }
    } catch (err) {
      console.error('Session check failed:', err);
      clearToken();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Authenticate with wallet signature
   */
  const authenticate = useCallback(async (): Promise<boolean> => {
    console.log('[Auth] Starting authentication...');

    if (!publicKey || !signMessage) {
      console.log('[Auth] No publicKey or signMessage available');
      setError('Wallet not connected');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const walletAddress = publicKey.toBase58();
      console.log('[Auth] Wallet address:', walletAddress);

      // Step 1: Get challenge from backend
      console.log('[Auth] Step 1: Requesting challenge...');
      const challenge = await requestAuthChallenge(walletAddress);
      console.log('[Auth] Got challenge, nonce:', challenge.nonce);

      // Step 2: Sign the message with wallet
      console.log('[Auth] Step 2: Requesting wallet signature...');
      const messageBytes = new TextEncoder().encode(challenge.message);
      const signatureBytes = await signMessage(messageBytes);
      const signature = bs58.encode(signatureBytes);
      console.log('[Auth] Got signature:', signature.substring(0, 20) + '...');

      // Step 3: Verify signature with backend and get session
      console.log('[Auth] Step 3: Verifying with backend...');
      const session = await verifySignature(
        walletAddress,
        signature,
        challenge.nonce
      );
      console.log('[Auth] Verification successful, user:', session.user);

      setUser(session.user);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      setError(message);
      console.error('[Auth] Authentication error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, signMessage]);

  /**
   * Logout and clear session
   */
  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      clearToken();
    }
  };

  const logout = useCallback(async () => {
    await handleLogout();
    // Also disconnect wallet
    if (disconnect) {
      await disconnect();
    }
  }, [disconnect]);

  /**
   * Refresh session data
   */
  const refreshSession = useCallback(async () => {
    try {
      const session = await validateSession();
      if (session.valid && session.user) {
        setUser(session.user);
      }
    } catch (err) {
      console.error('Session refresh failed:', err);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        error,
        authenticate,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
